import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Link as MuiLink,
} from '@mui/material';
import { CheckCircle, Email, PrivacyTip, Extension } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignIn from '../components/GoogleSignIn';
import { trackAuthEvent, trackButtonClick } from '../utils/analytics';

const LoginPage: React.FC = () => {
  const { loginWithGoogle, isLoading, error, clearError } = useAuth();

  const handleGoogleSuccess = async (credential: string) => {
    trackAuthEvent('sign_in_attempt', 'google');
    try {
      await loginWithGoogle(credential, 'Web Browser');
      trackAuthEvent('sign_in_success', 'google');
    } catch (err) {
      trackAuthEvent('sign_in_error', 'google');
      // Error is handled by AuthContext
    }
  };

  const handleGoogleError = (error: string) => {
    clearError();
    // You could show a toast or handle the error here
    console.error('Google Sign-in error:', error);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: { xs: 4, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 0 }
        }}
      >
        <Paper elevation={3} sx={{ padding: { xs: 2, sm: 4 }, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <img 
              src="/logo192.png" 
              alt="Bufferio App" 
              style={{ width: window.innerWidth < 600 ? 80 : 120, height: window.innerWidth < 600 ? 80 : 120 }}
            />
            <Typography component="h1" variant={window.innerWidth < 600 ? "h5" : "h4"} sx={{ mt: 2, mb: 1 }}>
              Bufferio App
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share clipboard content across your devices
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <GoogleSignIn
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={isLoading}
            />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Sign in with your Google account to continue
            </Typography>
          </Box>
        </Paper>

        <Card sx={{ mt: { xs: 2, sm: 3 }, width: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
              What is Bufferio?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' }, mb: 2 }}>
              Bufferio is a clipboard sharing service that lets you seamlessly transfer text, images, and files between your devices using Web and Chrome Extension.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '0.938rem', sm: '1.125rem' }, fontWeight: 600, mt: 2 }}>
              How it works:
            </Typography>
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Send to your devices"
                  secondary="Instantly share content to any of your registered devices"
                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Send to anyone"
                  secondary="Generate a 6-digit code to share content with non-registered devices"
                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Multiple formats"
                  secondary="Share text, images, and files up to 100MB"
                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            </List>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Extension />}
                component={MuiLink}
                href="https://chromewebstore.google.com/detail/fpoefldafmjaalhaegehkgjgabkgfalh?utm_source=item-share-cb"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  textTransform: 'none',
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
                onClick={() => trackButtonClick('Chrome Extension', 'Login Page')}
              >
                Install Chrome Extension
              </Button>
              
              <Button
                variant="contained"
                fullWidth
                startIcon={<Email />}
                component={MuiLink}
                href="mailto:support@bufferio.app"
                sx={{ 
                  textTransform: 'none',
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' }
                }}
                onClick={() => trackButtonClick('Contact Support', 'Login Page')}
              >
                Contact Support
              </Button>
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PrivacyTip />}
                component={Link}
                to="/privacy"
                sx={{ 
                  textTransform: 'none',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(33, 150, 243, 0.1)'
                  }
                }}
                onClick={() => trackButtonClick('Privacy Policy', 'Login Page')}
              >
                Privacy Policy
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginPage;
